import { useState } from 'react';
import { type FormatOptionLabelMeta } from 'react-select';
import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title/Title';
import IconAdd from '@/icons/Add.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateArticleSubLocation } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useCurrentSession } from '@/services/auth';

type SubLocationOption = { value: string; label: string; isStickyOption?: true };

export const ArticleSubLocation = () => {
  const { article, request } = useArticleContext();
  const { currentSession } = useCurrentSession();
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const subLocationOptions = article.atEntity?.placeConfig?.subLocationOptions ?? [];
  const entityName = article.atEntity?.name;

  const isUserAtEntity = currentSession?.roles.some((role) => role.entityId === article.atEntityId);

  const { mutate: deleteSubLocation, isPending: isDeletingSubLocation } =
    useUpdateArticleSubLocation({
      articleId: article.id,
      requestId: request.id,
    });

  if (!isUserAtEntity || !article.atEntity) return null;

  if (article.subLocation) {
    return (
      <Stack row alignItems="center" gap="0.25rem">
        <InlineText
          size="200"
          className="text-secondary"
          aria-label="sub-location"
          style={{ paddingLeft: '1.25rem' }}
        >
          {article.subLocation}
        </InlineText>
        <Stack row alignItems="center">
          <Button
            size="small"
            variant="secondary"
            onPress={() => setOpen(!open)}
            iconOnly
            noBorder
            tooltip={t({
              id: 'components.request-location.sublocation.edit',
              message: 'Edit sub-location',
            })}
            ariaLabel={t({
              id: 'components.request-location.sublocation.edit',
              message: 'Edit sub-location',
            })}
          >
            <IconEdit className="text-secondary" />
          </Button>
          <Button
            size="small"
            variant="secondary"
            onPress={() => {
              deleteSubLocation({ subLocation: null });
            }}
            disabled={isDeletingSubLocation}
            iconOnly
            noBorder
            tooltip={t({
              id: 'components.request-location.sublocation.delete-button',
              message: 'Delete sub-location',
            })}
            ariaLabel={t({
              id: 'components.request-location.sublocation.delete-button',
              message: 'Delete sub-location',
            })}
          >
            <IconCross className="text-secondary" />
          </Button>
        </Stack>
        <ArticleSubLocationDrawer
          open={open}
          setOpen={setOpen}
          initialSubLocation={article.subLocation}
          subLocationOptions={subLocationOptions}
          entityName={entityName}
          articleId={article.id}
          requestId={request.id}
        />
      </Stack>
    );
  }

  return (
    <Stack>
      <Button
        variant="link-subtle"
        size="small"
        noBorder
        className="text-secondary"
        style={{ paddingTop: '.5rem' }}
        onPress={() => setOpen(!open)}
      >
        <IconAdd />
        <Trans id="components.request-location.sublocation.button">Add a sub-location</Trans>
      </Button>
      <ArticleSubLocationDrawer
        open={open}
        setOpen={setOpen}
        subLocationOptions={subLocationOptions}
        entityName={entityName}
        articleId={article.id}
        requestId={request.id}
      />
    </Stack>
  );
};

const ArticleSubLocationDrawer = ({
  open,
  setOpen,
  initialSubLocation,
  subLocationOptions,
  entityName,
  articleId,
  requestId,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialSubLocation?: string | null;
  subLocationOptions: string[];
  entityName: string | undefined;
  articleId: string;
  requestId: string;
}) => {
  return (
    <Drawer
      style={{ maxWidth: '31.875rem' }}
      onOpenChange={(isOpen) => {
        if (!isOpen) setOpen(false);
      }}
      isOpen={open}
    >
      {open && (
        <ArticleSubLocationDrawerContent
          setOpen={setOpen}
          initialSubLocation={initialSubLocation}
          subLocationOptions={subLocationOptions}
          entityName={entityName}
          articleId={articleId}
          requestId={requestId}
        />
      )}
    </Drawer>
  );
};

const ArticleSubLocationDrawerContent = ({
  setOpen,
  initialSubLocation,
  subLocationOptions,
  entityName,
  articleId,
  requestId,
}: {
  setOpen: (open: boolean) => void;
  initialSubLocation?: string | null;
  subLocationOptions: string[];
  entityName: string | undefined;
  articleId: string;
  requestId: string;
}) => {
  const isKnownOption =
    initialSubLocation != null && subLocationOptions.includes(initialSubLocation);

  const [currentSubLocation, setCurrentSubLocation] = useState<string | undefined>(
    subLocationOptions.length === 0
      ? 'other'
      : initialSubLocation == null
        ? undefined
        : isKnownOption
          ? initialSubLocation
          : 'other'
  );

  const [otherSubLocation, setOtherSubLocation] = useState<string | undefined>(
    isKnownOption ? undefined : (initialSubLocation ?? undefined)
  );

  const { t } = useLingui();

  const OTHER_OPTION: SubLocationOption = {
    value: 'other',
    label: t({
      id: 'components.request-location.sublocation.other',
      message: 'Other (specify)',
    }),
    isStickyOption: true,
  };

  const selectOptions: SubLocationOption[] = [
    ...subLocationOptions.map((name) => ({ value: name, label: name })),
    OTHER_OPTION,
  ];

  const selectedOption =
    selectOptions.find((option) => option.value === currentSubLocation) ?? null;

  const isOther = currentSubLocation === OTHER_OPTION.value;

  const subLocationValue = isOther ? (otherSubLocation ?? null) : (currentSubLocation ?? null);

  const { mutate: updateSubLocation, isPending } = useUpdateArticleSubLocation({
    articleId,
    requestId,
  });

  const handleSave = () => {
    updateSubLocation({ subLocation: subLocationValue }, { onSuccess: () => setOpen(false) });
  };

  return (
    <>
      <DrawerHeader>
        <Title as="h1" size="100">
          {initialSubLocation ? (
            <Trans id="components.request-location.sublocation.edit">Edit sub-location</Trans>
          ) : (
            <Trans id="components.request-location.sublocation.button">Add a sub-location</Trans>
          )}
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack gap="1rem" style={{ padding: '2rem' }}>
          <InputSearchSelect
            label={<Trans id="components.request-location.sublocation.title">Sub location</Trans>}
            isDisabled={subLocationOptions.length === 0}
            placeholder={
              <Trans id="components.request-location.sublocation.select">Select an option</Trans>
            }
            value={selectedOption}
            options={selectOptions}
            onChange={(option) => {
              setCurrentSubLocation(option?.value ?? undefined);
              setOtherSubLocation(undefined);
            }}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            formatOptionLabel={(option, { context }: FormatOptionLabelMeta<SubLocationOption>) => {
              if (option.value === OTHER_OPTION.value && context === 'menu') {
                return (
                  <Stack row gap="0.5rem" alignItems="center" style={{ flex: 1 }}>
                    <IconAdd style={{ fontSize: '1.25rem' }} />
                    <Paragraph>{option.label}</Paragraph>
                  </Stack>
                );
              }

              return option.label;
            }}
          />
          {isOther && (
            <InputText
              label={
                <Trans id="components.request-location.sublocation.name">Sub-location name</Trans>
              }
              placeholder={t({
                id: 'components.request-location.sublocation.name.placeholder',
                message: 'Specify the sub location name',
              })}
              value={otherSubLocation ?? ''}
              onChange={setOtherSubLocation}
            />
          )}
          <AlertBar size="large" type="info">
            <Trans id="components.request-location.sublocation.info">
              The sub-location is only visible to the {entityName} entity while the item is at this
              location.
            </Trans>
          </AlertBar>
        </Stack>
      </DrawerBody>
      <Footer>
        <FooterButtons>
          <Button variant="secondary" onPress={() => setOpen(false)}>
            <Trans id="components.request-location.sublocation.actions.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            onPress={handleSave}
            disabled={isPending || !subLocationValue || subLocationValue === initialSubLocation}
          >
            <Trans id="components.request-location.sublocation.actions.add">Add</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
